import React, { Fragment, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Nav, NavItem, NavLink, Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { keyBy, groupBy, pick, sortBy, orderBy, escape } from 'lodash';
import { format as formatDate, isAfter, endOfDay } from 'date-fns';
import classnames from 'classnames';
import qs from 'qs';
import { toast } from 'react-toastify';
import copy from 'copy-to-clipboard';

import firebase from '../../firebase';
import { canUpdateTask, canDeleteTask } from '../../shared/abilities';
import { statuses, activityTypes, hasUnreadComment, taskFields, commentFields, remindTimingHours, remindTimingLabel, taskRemindTimingFields } from '../../shared/models/task';
import AdminPage from '../hocs/AdminPage';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useFirebaseUser from '../hooks/useFirebaseUser';
import useTenant from '../hooks/useTenant';
import QuerySelector from '../QuerySelector';
import QuerySortLabel from '../QuerySortLabel';
import ModalButton from '../ModalButton';
import ModelFormModal from '../modals/ModelFormModal';
import AddInTenantButton from '../AddInTenantButton';
import Activity from '../Activity';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import RichTextContent from '../RichTextContent';

const { entries } = Object;
const db = firebase.firestore();
const tasksRef = db.collection('tasks');
const usersRef = db.collection('users');
const activitiesRef = db.collection('activities');
const taskRemindTimingsRef = db.collection('taskRemindTimings');

const tabs = {
  mine: { label: 'マイタスク' },
  all: { label: '全員のタスク' },
  created: { label: '依頼したタスク' },
};

const formatDueDate = (dueDate) => (dueDate ? formatDate(dueDate.toDate(), 'yyyy/MM/dd HH:mm') : '期限なし');

// 登録元パスのラベル。末尾40文字程度に丸め、先頭が切れたら … を付ける。
const formatContextPath = (path) => {
  if (!path) return null;
  const max = 40;
  return path.length > max ? `…${path.slice(-max)}` : path;
};

// リッチテキスト化以前の説明はプレーンテキストなので、改行を段落にしてHTMLとして扱う
const descriptionHtml = (description) => {
  if (!description || /<[a-z][\s\S]*>/i.test(description)) return description;
  return description.split('\n').map((_) => `<p>${escape(_)}</p>`).join('');
};

// アクティビティを記録する。tenantId/createdBy/createdAt を付与。
const createActivity = async (tenant, user, type, payload) => {
  await activitiesRef.add({
    ...(tenant && { tenantId: tenant.id }),
    type,
    payload,
    createdBy: user,
    createdAt: new Date(),
  });
};

export default AdminPage(function AdminTasks(props) {
  const { user, location, history } = props;
  const tenant = useTenant();
  const { firebaseUser } = useFirebaseUser();
  const queryParams = qs.parse(decodeURI(location.search.slice(1)));
  const { tab = 'mine', statuses: statusFilter = ['open'], assignees = [], taskId: openTaskId } = queryParams;
  const { field: sortField = 'createdAt', direction: sortDirection = 'desc' } = queryParams.sort || {};
  const sortTasks = (tasks) => orderBy(tasks, [(_) => !_[sortField], (_) => _[sortField]?.toMillis?.() || 0], ['asc', sortDirection]);

  // タスク詳細を開くURL（クエリにtaskIdを足す）。閉じる時はtaskIdを外す。
  const taskUrl = (taskId) => `${location.pathname}?${qs.stringify({ ...queryParams, taskId })}`;
  const closeUrl = `${location.pathname}?${qs.stringify({ ...queryParams, taskId: undefined })}`;
  const closeTask = () => history.push(closeUrl);

  const tasks = useCollectionSubscriptionInTenant(tasksRef);
  const users = useCollectionSubscriptionInTenant(usersRef.where('role', 'in', ['admin', 'staff']));
  // タスク関連のアクティビティ（削除可否判定用）。
  // activities は全体で数万件あるため全件購読は不可。payload.taskId を持つものだけをクエリで絞る。
  const taskActivities = useCollectionSubscriptionInTenant(activitiesRef.orderBy('payload.taskId'));
  const activitiesByTaskId = groupBy(taskActivities, (_) => _.payload.taskId);
  const usersByUid = keyBy(users, 'uid');
  const assigneeOptions = users.map((_) => ({ label: _.displayName, value: _.uid }));
  const statusOptions = entries(statuses).map(([value, { label }]) => ({ label, value }));
  const taskRemindTimings = sortBy(useCollectionSubscriptionInTenant(taskRemindTimingsRef), remindTimingHours);
  const taskRemindTimingsById = keyBy(taskRemindTimings, 'id');
  const remindTimingOptions = taskRemindTimings.map((_) => ({ label: remindTimingLabel(_), value: _.id }));

  // タブによる母集団の絞り込み
  const tabFiltered = tasks.filter((_) => {
    if (tab === 'mine') return _.assignee?.uid === user.uid;
    if (tab === 'created') return _.addedBy?.uid === user.uid;
    return true;
  });
  // 絞り込みUI（ステータス・担当）
  const filtered = tabFiltered.filter((_) => {
    if (statusFilter.length > 0 && !statusFilter.includes(_.status)) return false;
    if (assignees.length > 0 && !assignees.includes(_.assignee?.uid)) return false;
    return true;
  });

  // 多階層ツリー構築
  const tasksById = keyBy(tasks, 'id');
  const visibleIds = new Set(filtered.map((_) => _.id));
  const childrenByParentId = groupBy(
    tasks.filter((_) => _.parentTaskId),
    'parentTaskId'
  );

  const onToggleStatus = async (task) => {
    const toDone = task.status !== 'done';
    if (toDone) {
      // 未完了の子タスクが残っている親は完了にできない
      const hasOpenChild = (childrenByParentId[task.id] || []).some((_) => _.status !== 'done');
      if (hasOpenChild) {
        toast.error('未完了の子タスクがあるため完了にできません');
        return;
      }
      if (!window.confirm('このタスクを完了にしますか？')) return;
    }
    try {
      await task.ref.update({
        status: toDone ? 'done' : 'open',
        completedAt: toDone ? new Date() : null,
        completedBy: toDone ? pick(firebaseUser, ['uid', 'email', 'displayName']) : null,
      });
      await createActivity(tenant, user, toDone ? 'completeTask' : 'uncompleteTask', { taskId: task.id, taskTitle: task.title });
      toast.success(toDone ? 'タスクを完了しました' : '未完了に戻しました');
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  // 複製1件分のドキュメント値を組み立て。未完了・新規メタで作り直し、親は呼び出し側が指定。
  const duplicateValues = (task, { newTitle, parentTaskId }) => ({
    title: newTitle ?? task.title,
    description: task.description || null,
    assignee: task.assignee || null,
    dueDate: task.dueDate || null,
    remindTimingIds: task.remindTimingIds || [],
    parentTaskId: parentTaskId ?? task.parentTaskId ?? null,
    status: 'open',
    completedAt: null,
    completedBy: null,
    tenantId: tenant.id,
    addedBy: pick(firebaseUser, ['uid', 'email', 'displayName']),
    createdAt: new Date(),
  });

  // タスク（とその子孫ツリー）を再帰的に複製。生成IDを子の親に付け替える。
  const duplicateTree = async (task, { newTitle, parentTaskId } = {}) => {
    const newRef = tasksRef.doc();
    await newRef.set(duplicateValues(task, { newTitle, parentTaskId }));
    await createActivity(tenant, user, 'duplicateTask', { taskId: newRef.id, taskTitle: newTitle ?? task.title });
    const children = sortBy(childrenByParentId[task.id] || [], (_) => _.createdAt?.toMillis?.() || 0);
    await Promise.all(children.map((_) => duplicateTree(_, { parentTaskId: newRef.id })));
  };

  const onDuplicate = async (task) => {
    const childCount = (childrenByParentId[task.id] || []).length;
    const message = childCount > 0 ? `このタスクと子タスク${childCount}件を複製しますか？` : 'このタスクを複製しますか？';
    if (!window.confirm(message)) return;
    try {
      await duplicateTree(task, { newTitle: `${task.title} のコピー` });
      toast.success('タスクを複製しました');
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  const onDelete = async (task) => {
    if (!window.confirm('このタスクを削除しますか？この操作は取り消せません。')) return;
    try {
      await createActivity(tenant, user, 'deleteTask', { taskId: task.id, taskTitle: task.title });
      await task.ref.delete();
      toast.success('タスクを削除しました');
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  // フォーム値をタスクドキュメントに整形
  const processValues = (values) => {
    const assigneeUser = values.assignee && usersByUid[values.assignee];
    return {
      title: values.title,
      description: values.description || null,
      assignee: assigneeUser ? pick(assigneeUser, ['uid', 'email', 'displayName']) : null,
      dueDate: values.dueDate || null,
      remindTimingIds: values.dueDate ? values.remindTimingIds || [] : [],
    };
  };

  const renderTask = (task, depth = 0) => {
    const children = sortTasks(childrenByParentId[task.id] || []);
    const isDone = task.status === 'done';
    const statusInfo = statuses[task.status] || statuses.open;
    // 未完了かつ期限が今日の終わり以前（=期限切れ または 今日が期限）なら強調
    const isOverdue = !isDone && task.dueDate && !isAfter(task.dueDate.toDate(), endOfDay(new Date()));
    // 未完了の子タスクがあると親は完了にできない
    const hasOpenChild = children.some((_) => _.status !== 'done');
    // 他者のアクティビティ（自分以外が行った操作・コメント）があると削除不可
    const hasOthersActivity = (activitiesByTaskId[task.id] || []).some((_) => _.createdBy?.uid !== user.uid);
    const canDelete = canDeleteTask(user, task) && children.length === 0 && !hasOthersActivity;
    // 子タスク作成用の参照
    const childTaskRef = tasksRef.doc();

    return (
      <Fragment key={task.id}>
        <tr className={isDone ? 'text-muted' : ''}>
          <td style={{ paddingLeft: 12 + depth * 24 }}>
            {depth > 0 && <span className="fas fa-level-up-alt fa-rotate-90 text-muted mr-2" />}
            <Link
              to={taskUrl(task.id)}
              className={classnames('text-primary', isDone ? 'text-muted text-decoration-line-through' : 'font-weight-bold')}
              title={`タスク詳細: ${task.title}`}
            >
              {task.title}
            </Link>
            {hasUnreadComment(task, user.uid) && (
              <Link to={taskUrl(task.id)} className="badge badge-info ml-2" title="未読のコメントがあります">
                <span className="fas fa-comment mr-1" />
                新着コメント
              </Link>
            )}
            {task.context?.currentPath && (
              <Link to={task.context.currentPath} className="badge badge-light ml-2" title={`関連画面へ: ${task.context.currentPath}`}>
                <span className="fas fa-link mr-1" />
                {formatContextPath(task.context.currentPath)}
              </Link>
            )}
          </td>
          <td>{task.addedBy?.displayName || <span className="text-muted">—</span>}</td>
          <td>{task.assignee?.displayName || <span className="text-muted">未割当</span>}</td>
          <td className={isOverdue ? 'text-danger font-weight-bold' : ''}>{formatDueDate(task.dueDate)}</td>
          <td>{task.createdAt && formatDate(task.createdAt.toDate(), 'yyyy/MM/dd HH:mm')}</td>
          <td>
            <Badge color={statusInfo.color}>{statusInfo.label}</Badge>
          </td>
          <td className="text-nowrap">
            <Button
              size="sm"
              color={isDone ? 'secondary' : 'success'}
              outline={isDone}
              className="mr-1"
              disabled={!isDone && hasOpenChild}
              title={!isDone && hasOpenChild ? '未完了の子タスクがあるため完了にできません' : undefined}
              onClick={() => onToggleStatus(task)}
            >
              <span className={classnames('fas mr-1', isDone ? 'fa-undo' : 'fa-check')} />
              {isDone ? '戻す' : '完了'}
            </Button>
            <AddInTenantButton
              size="sm"
              color="link"
              label="子タスク"
              icon={<span className="fas fa-plus mr-1" />}
              itemRef={childTaskRef}
              processValues={(values) => ({ ...processValues(values), parentTaskId: task.id, status: 'open' })}
              successMessage="子タスクを作成しました"
              onFinish={(values) => createActivity(tenant, user, 'createChildTask', { taskId: childTaskRef.id, taskTitle: values.title })}
              FormModal={ModelFormModal}
              formProps={{
                title: `子タスク作成（親: ${task.title}）`,
                submitLabel: '作成',
                fields: taskFields({ assigneeOptions, remindTimingOptions }),
              }}
            />
            {canUpdateTask(user, task) && (
              <Button
                size="sm"
                color="link"
                title="このタスク（と子タスク）を複製"
                onClick={() => onDuplicate(task)}
              >
                <span className="fas fa-copy mr-1" />
                複製
              </Button>
            )}
            <Button
              size="sm"
              color="link"
              title="このタスクへのURLをコピー"
              onClick={() => {
                copy(`${window.location.origin}${taskUrl(task.id)}`);
                toast.success('URLをコピーしました');
              }}
            >
              <span className="fas fa-link" />
            </Button>
            {canDelete && (
              <Button
                size="sm"
                color="link"
                className="text-danger"
                title="このタスクを削除"
                onClick={() => onDelete(task)}
              >
                <span className="fas fa-trash mr-1" />
                削除
              </Button>
            )}
          </td>
        </tr>
        {children.map((_) => renderTask(_, depth + 1))}
      </Fragment>
    );
  };

  // ルート（親が表示対象外、または親なし）を起点に描画
  const roots = sortTasks(filtered.filter((_) => !_.parentTaskId || !visibleIds.has(_.parentTaskId) || !tasksById[_.parentTaskId]));

  return (
    <div className="admin-tasks container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">タスク管理</h4>
        <ModalButton
          title="リマインド時期設定"
          content={(_) => <TaskRemindTimings user={user} taskRemindTimings={taskRemindTimings} />}
          modalProps={{ style: { minWidth: 600 } }}
        >
          <span className="fas fa-bell mr-1" />
          リマインド時期設定
        </ModalButton>
      </div>

      <Nav tabs>
        {entries(tabs).map(([name, { label }]) => (
          <NavItem key={name}>
            <NavLink
              tag={Link}
              className={classnames({ active: tab === name })}
              to={`${location.pathname}?${qs.stringify({ ...queryParams, tab: name })}`}
            >
              {label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <div className="d-flex flex-wrap gap-2 my-3">
        <QuerySelector paramName="statuses" width={180} isMulti options={statusOptions} label="状態で絞込み" defaultValue={['open']} />
        <QuerySelector paramName="assignees" width={220} isMulti options={assigneeOptions} label="担当で絞込み" />
      </div>

      <table className="table table-bordered bg-white">
        <thead className="thead-light">
          <tr>
            <th>タスク</th>
            <th style={{ width: 140 }}>依頼者</th>
            <th style={{ width: 140 }}>担当</th>
            <th style={{ width: 150 }}>
              <QuerySortLabel field="dueDate" defaultDirection="asc">期限</QuerySortLabel>
            </th>
            <th style={{ width: 150 }}>
              <QuerySortLabel field="createdAt" defaultActive defaultDirection="desc">作成日</QuerySortLabel>
            </th>
            <th style={{ width: 90 }}>状態</th>
            <th style={{ width: 390 }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {roots.length > 0 ? (
            roots.map((_) => renderTask(_, 0))
          ) : (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                タスクはありません
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* タスク詳細ダイアログ（クエリパラメータ taskId 駆動） */}
      <Modal isOpen={!!openTaskId && !!tasksById[openTaskId]} toggle={closeTask} size="lg" scrollable>
        {tasksById[openTaskId] && (
          <Fragment>
            <ModalHeader toggle={closeTask}>
              {tasksById[openTaskId].title}
              <Button
                color="link"
                size="sm"
                className="ml-2 p-0 align-baseline"
                title="このタスクへのURLをコピー"
                onClick={() => {
                  copy(`${window.location.origin}${taskUrl(openTaskId)}`);
                  toast.success('URLをコピーしました');
                }}
              >
                <span className="fas fa-link mr-1" />
                URLコピー
              </Button>
            </ModalHeader>
            <ModalBody>
              <TaskDetailContent task={tasksById[openTaskId]} tenant={tenant} user={user} usersByUid={usersByUid} assigneeOptions={assigneeOptions} remindTimingOptions={remindTimingOptions} taskRemindTimingsById={taskRemindTimingsById} firebaseUser={firebaseUser} />
            </ModalBody>
            <ModalFooter>
              <Button className="cancel" onClick={closeTask}>閉じる</Button>
            </ModalFooter>
          </Fragment>
        )}
      </Modal>
    </div>
  );
});

function TaskDetailContent({ task, tenant, user, usersByUid, assigneeOptions, remindTimingOptions, taskRemindTimingsById, firebaseUser }) {
  const commentsRef = task.ref.collection('taskComments');
  // コメント（orderByなしで取得しクライアントソート）
  const unsortedComments = useCollectionSubscriptionInTenant(commentsRef);
  const comments = sortBy(unsortedComments, (_) => _.createdAt?.toMillis?.() || 0);
  // この対象タスクのアクティビティ
  const unsortedActivities = useCollectionSubscriptionInTenant(activitiesRef.where('payload.taskId', '==', task.id));
  const activities = sortBy(unsortedActivities, (_) => _.createdAt?.toMillis?.() || 0).reverse();

  // 編集時に紐づけON/OFFを切り替えるための、現在の作成元パス
  const contextPath = task.context?.currentPath;

  useEffect(() => {
    if (!hasUnreadComment(task, user.uid)) return;

    task.ref.update({ [`commentReadAtByUid.${user.uid}`]: new Date() });
  }, [task.id, task.lastCommentedAt?.toMillis()]);

  const onSubmitEdit = async (values, { onClickClose }) => {
    const assigneeUser = values.assignee && usersByUid[values.assignee];
    try {
      await task.ref.update({
        title: values.title,
        description: values.description || null,
        assignee: assigneeUser ? pick(assigneeUser, ['uid', 'email', 'displayName']) : null,
        dueDate: values.dueDate || null,
        remindTimingIds: values.dueDate ? values.remindTimingIds || [] : [],
        // チェックを外したら作成元を消す（contextPath がある場合のみ操作対象）
        ...(contextPath && { context: values.attachContext ? task.context : null }),
        updatedBy: pick(firebaseUser, ['uid', 'email', 'displayName']),
      });
      await createActivity(tenant, user, 'updateTask', { taskId: task.id, taskTitle: values.title });
      toast.success('更新しました');
      onClickClose();
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  return (
    <div>
      <dl className="row">
        <dt className="col-3">説明</dt>
        <dd className="col-9">{task.description ? <RichTextContent html={descriptionHtml(task.description)} /> : '—'}</dd>
        <dt className="col-3">担当</dt>
        <dd className="col-9">{task.assignee?.displayName || '未割当'}</dd>
        <dt className="col-3">期限</dt>
        <dd className="col-9">{formatDueDate(task.dueDate)}</dd>
        <dt className="col-3">リマインド</dt>
        <dd className="col-9">
          {(task.remindTimingIds || []).filter((_) => taskRemindTimingsById[_]).map((_) => remindTimingLabel(taskRemindTimingsById[_])).join(', ') || '—'}
        </dd>
        <dt className="col-3">状態</dt>
        <dd className="col-9">{(statuses[task.status] || statuses.open).label}</dd>
        <dt className="col-3">作成者</dt>
        <dd className="col-9">{task.addedBy?.displayName || '—'}</dd>
      </dl>

      <ModalButton
        Component={Button}
        color="primary"
        size="sm"
        className="mb-3"
        title="タスク編集"
        Modal={ModelFormModal}
        modalProps={{
          title: 'タスク編集',
          submitLabel: '更新',
          values: {
            title: task.title,
            description: descriptionHtml(task.description),
            assignee: task.assignee?.uid,
            dueDate: task.dueDate ? task.dueDate.toDate() : null,
            remindTimingIds: task.remindTimingIds || [],
            // 作成元が紐づいていれば初期ON
            attachContext: !!contextPath,
          },
          fields: taskFields({ assigneeOptions, contextPath, remindTimingOptions }),
          onSubmit: onSubmitEdit,
        }}
      >
        <span className="fas fa-edit mr-1" />
        編集
      </ModalButton>

      <hr />
      <h6>コメント</h6>
      <div className="mb-3">
        {comments.length === 0 && <div className="text-muted small">コメントはありません</div>}
        {comments.map(({ id, body, createdAt, createdBy }) => (
          <div key={id} className="card p-2 mb-2">
            <div className="small text-muted mb-1">
              {createdBy?.displayName} {createdAt && formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
            </div>
            <div className="text-break" style={{ whiteSpace: 'pre-line' }}>{body}</div>
          </div>
        ))}
      </div>
      <AddInTenantButton
        label="コメント追加"
        itemRef={commentsRef.doc()}
        processValues={(_) => ({ ..._, createdBy: pick(firebaseUser, ['uid', 'email', 'displayName']) })}
        onFinish={async () => {
          await task.ref.update({
            lastCommentedAt: new Date(),
            lastCommentedBy: pick(firebaseUser, ['uid', 'email', 'displayName']),
          });
          await createActivity(tenant, user, 'addTaskComment', { taskId: task.id, taskTitle: task.title });
        }}
        FormModal={ModelFormModal}
        formProps={{ title: 'コメント追加', fields: commentFields() }}
      />

      <hr />
      <h6>アクティビティ</h6>
      <div className="overflow-auto" style={{ maxHeight: 320 }}>
        {activities.length === 0 ? (
          <div className="text-muted small">アクティビティはありません</div>
        ) : (
          <div className="d-flex flex-column gap-1">
            {activities.map((_) => (
              <Activity key={_.id} activity={_} activityTypes={activityTypes} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRemindTimings({ user, taskRemindTimings }) {
  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <AddInTenantButton
          itemRef={taskRemindTimingsRef.doc()}
          FormModal={ModelFormModal}
          formProps={{ title: 'リマインド時期追加', fields: taskRemindTimingFields() }}
          disabled={!canUpdateTask(user)}
        />
      </div>
      {taskRemindTimings.length > 0 ? (
        <table className="table">
          <thead className="thead-light text-center">
            <tr>
              <th>リマインド時期</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {taskRemindTimings.map((taskRemindTiming) => {
              const { id, ref } = taskRemindTiming;
              return (
                <tr key={id}>
                  <td>期限の{remindTimingLabel(taskRemindTiming)}</td>
                  <td className="text-nowrap text-right">
                    <EditButton
                      itemRef={ref}
                      FormModal={ModelFormModal}
                      formProps={{ title: 'リマインド時期編集', fields: taskRemindTimingFields() }}
                      disabled={!canUpdateTask(user)}
                    />
                    <DeleteButton itemRef={ref} className="ml-2" disabled={!canUpdateTask(user)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div>リマインド時期は未登録です</div>
      )}
    </div>
  );
}
